import { CurrencyPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoDirective } from '@jsverse/transloco';
import { errorIcon } from '@ld.consumer-portal/data-uri';
import {
  CreditCardPaymentRequest,
  CreditCardPaymentResponse,
  CreditConfigurationInfo,
  EXPIRATION_MONTHS,
  NonPciCreditCardPaymentRequest,
  PciOpaqueData,
  US_STATES,
} from '@ld.consumer-portal/models';
import { AcceptJsLoaderService } from '@ld.consumer-portal/utils';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

function cardNumberValidator(control: AbstractControl): ValidationErrors | null {
  const raw = (control.value || '').replace(/\D/g, '');
  if (!raw) return null;
  if (raw.length < 15 || raw.length > 16) return { cardNumberLength: true };
  return null;
}

export interface CreditCardPaymentSuccess {
  transactionId: string;
  amount: number;
}

@Component({
  selector: 'ld-credit-card-modal',
  imports: [
    BaseModalComponent,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    CurrencyPipe,
    DatePipe,
    TranslocoDirective,
  ],
  templateUrl: './credit-card-modal.component.html',
  styleUrl: './credit-card-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditCardModalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly acceptJsLoader = inject(AcceptJsLoaderService);

  protected readonly errorIcon = errorIcon;

  readonly loanId = input.required<string>();

  readonly borrowerId = input.required<string>();

  readonly amount = input.required<number>();

  readonly firstName = input<string>('');

  readonly lastName = input<string>('');

  readonly address = input<string>('');

  readonly city = input<string>('');

  readonly state = input<string>('');

  readonly zip = input<string>('');

  readonly creditConfig = input<CreditConfigurationInfo>();

  readonly lookupZipFn = input.required<(zip: string) => Promise<{ city: string; state: string } | null>>();

  readonly submitPaymentFn = input.required<
    (loanId: string, borrowerId: string, request: CreditCardPaymentRequest) => Promise<CreditCardPaymentResponse>
  >();

  readonly submitNonPciPaymentFn = input<
    (loanId: string, borrowerId: string, request: NonPciCreditCardPaymentRequest) => Promise<CreditCardPaymentResponse>
  >();

  readonly success = output<CreditCardPaymentSuccess>();

  readonly embedded = input<boolean>(false);

  readonly cancel = output<void>();

  readonly error = output<string>();

  protected readonly currentStep = signal<'form' | 'review' | 'processing' | 'complete' | 'error'>('form');

  protected readonly isLoading = signal(false);

  protected readonly errorMessage = signal<string | null>(null);

  private pciOpaqueData: PciOpaqueData[] = [];

  private pendingSuccessData: CreditCardPaymentSuccess | null = null;

  protected readonly paymentResponse = signal<CreditCardPaymentResponse | null>(null);

  protected readonly consentChecked = signal(false);

  protected readonly paymentDate = signal<Date>(new Date());

  protected readonly isPciEnabled = computed(() => this.creditConfig()?.isPciEnabled ?? true);

  protected readonly cardType = signal<'visa' | 'mastercard' | 'amex' | 'discover' | ''>('');

  protected readonly cardTypeLabel = computed(() => {
    switch (this.cardType()) {
      case 'visa':
        return 'Visa';
      case 'mastercard':
        return 'MasterCard';
      case 'amex':
        return 'Amex';
      case 'discover':
        return 'Discover';
      default:
        return '';
    }
  });

  protected readonly states = US_STATES;
  protected readonly months = EXPIRATION_MONTHS;

  protected readonly years = computed(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => (currentYear + i).toString());
  });

  protected readonly cardForm = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    cardNumber: ['', [Validators.required, cardNumberValidator]],
    cvv: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(4), Validators.pattern(/^\d+$/)]],
    expirationMonth: ['', Validators.required],
    expirationYear: ['', Validators.required],
    address: ['', Validators.required],
    city: ['', Validators.required],
    state: ['', Validators.required],
    zip: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5), Validators.pattern(/^\d+$/)]],
  });

  protected get maskedCardNumber(): string {
    const cardNumber = (this.cardForm.get('cardNumber')?.value || '').replace(/\D/g, '');
    return this.acceptJsLoader.maskCardNumber(cardNumber);
  }

  async ngOnInit(): Promise<void> {
    this.cardForm.patchValue({
      firstName: this.firstName(),
      lastName: this.lastName(),
      address: this.address(),
      city: this.city(),
      state: this.state(),
      zip: this.zip(),
    });

    if (this.isPciEnabled()) {
      await this.loadAcceptJs();
    }
  }

  private async loadAcceptJs(): Promise<void> {
    try {
      this.isLoading.set(true);

      const creditConfigInfo = this.creditConfig();
      if (!creditConfigInfo?.configurations?.length) {
        throw new Error('Credit card configuration not provided');
      }

      const providerAddress = creditConfigInfo.providerAddress;
      if (!providerAddress) {
        throw new Error('Accept.js address not available in credit configuration response');
      }

      await this.acceptJsLoader.loadFromUrl(providerAddress);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load payment configuration';
      this.errorMessage.set(message);
      this.currentStep.set('error');
    } finally {
      this.isLoading.set(false);
    }
  }

  protected onCardNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/\D/g, '').slice(0, 16);
    const formatted = raw.replace(/(\d{4})(?=\d)/g, '$1-');
    input.value = formatted;
    this.cardForm.get('cardNumber')?.setValue(formatted, { emitEvent: false });
    this.cardType.set(this.acceptJsLoader.detectCardType(raw));
  }

  protected async onZipChange(): Promise<void> {
    const zip = this.cardForm.get('zip')?.value;
    if (zip && zip.length === 5) {
      const result = await this.lookupZipFn()(zip);
      if (result) {
        this.cardForm.patchValue({
          city: result.city,
          state: result.state,
        });
      }
    }
  }

  protected async onReview(): Promise<void> {
    const creditConfigInfo = this.creditConfig();
    const pciEnabled = this.isPciEnabled();

    if (!this.cardForm.valid) return;
    if (pciEnabled && !creditConfigInfo?.configurations?.length) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const formValue = this.cardForm.value;
      const rawCardNumber = (formValue.cardNumber || '').replace(/\D/g, '');

      if (pciEnabled && creditConfigInfo?.configurations?.length) {
        const monthString = formValue.expirationMonth || '';
        const monthNumber = parseInt(monthString, 10).toString();

        this.pciOpaqueData = await this.acceptJsLoader.tokenizeAll(
          creditConfigInfo.configurations,
          rawCardNumber,
          monthNumber,
          formValue.expirationYear || '',
          formValue.cvv || '',
        );
      }

      this.cardType.set(this.acceptJsLoader.detectCardType(rawCardNumber));
      this.consentChecked.set(false);
      this.paymentDate.set(new Date());
      this.currentStep.set('review');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Credit information could not be processed. Please review and try again.';
      this.errorMessage.set(message);
    } finally {
      this.isLoading.set(false);
    }
  }

  protected onBackToForm(): void {
    this.currentStep.set('form');
    this.consentChecked.set(false);
    this.pciOpaqueData = [];
  }

  protected onConsentChange(checked: boolean): void {
    this.consentChecked.set(checked);
  }

  protected async onSubmitPayment(): Promise<void> {
    const pciEnabled = this.isPciEnabled();

    if (pciEnabled && !this.pciOpaqueData.length) {
      this.errorMessage.set('Card data not tokenized. Please try again.');
      this.currentStep.set('form');
      return;
    }

    this.currentStep.set('processing');
    this.isLoading.set(true);

    try {
      const formValue = this.cardForm.value;
      const cardNumber = (formValue.cardNumber || '').replace(/\D/g, '');

      let response: CreditCardPaymentResponse;

      if (pciEnabled) {
        const request: CreditCardPaymentRequest = {
          firstName: formValue.firstName || '',
          lastName: formValue.lastName || '',
          cardNumber: cardNumber.slice(-4),
          cardType: this.cardTypeLabel(),
          cardId: '',
          address: {
            address1: formValue.address || '',
            city: formValue.city || '',
            state: formValue.state || '',
            zipCode: formValue.zip || '',
          },
          pciOpaqueData: this.pciOpaqueData,
        };
        response = await this.submitPaymentFn()(this.loanId(), this.borrowerId(), request);
      } else {
        const nonPciSubmitFn = this.submitNonPciPaymentFn();
        if (!nonPciSubmitFn) {
          throw new Error('Non-PCI payment submission is not configured');
        }
        const request: NonPciCreditCardPaymentRequest = {
          firstName: formValue.firstName || '',
          lastName: formValue.lastName || '',
          cardNumber,
          cardType: this.cardTypeLabel(),
          expirationMonth: parseInt(formValue.expirationMonth || '0', 10),
          expirationYear: parseInt(formValue.expirationYear || '0', 10),
          cvv: formValue.cvv || '',
          cardId: '',
          address: {
            address1: formValue.address || '',
            city: formValue.city || '',
            state: formValue.state || '',
            zipCode: formValue.zip || '',
          },
        };
        response = await nonPciSubmitFn(this.loanId(), this.borrowerId(), request);
      }

      this.paymentResponse.set(response);

      if (response.isSuccess) {
        this.pendingSuccessData = {
          transactionId: response.transactionId || '',
          amount: this.amount(),
        };
        this.currentStep.set('complete');
      } else if (response.isLoanLocked) {
        this.errorMessage.set(
          `Payment is currently locked by ${response.loanLockedBy || 'another user'}. Please try again later.`,
        );
        this.currentStep.set('error');
        this.error.emit(response.error || 'Loan is locked');
      } else {
        this.errorMessage.set(
          response.error || 'Payment could not be processed. Please review your payment information.',
        );
        this.currentStep.set('error');
        this.error.emit(response.error || 'Payment declined');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Payment failed';
      this.errorMessage.set(message);
      this.currentStep.set('error');
      this.error.emit(message);
    } finally {
      this.isLoading.set(false);
    }
  }

  protected onClose(): void {
    if (this.currentStep() === 'complete' && this.pendingSuccessData) {
      this.success.emit(this.pendingSuccessData);
      this.pendingSuccessData = null;
    } else {
      this.cancel.emit();
    }
  }

  protected onRetry(): void {
    this.currentStep.set('form');
    this.errorMessage.set(null);
    this.consentChecked.set(false);
    this.pciOpaqueData = [];
  }

  protected get nameOnCard(): string {
    const first = this.cardForm.get('firstName')?.value || '';
    const last = this.cardForm.get('lastName')?.value || '';
    return `${first} ${last}`.trim();
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && !this.isLoading()) {
      this.cancel.emit();
    }
  }

  protected getCardIcon(): string {
    switch (this.cardType()) {
      case 'visa':
        return 'credit_card';
      case 'mastercard':
        return 'credit_card';
      case 'amex':
        return 'credit_card';
      case 'discover':
        return 'credit_card';
      default:
        return 'credit_card';
    }
  }
}
